import React, { useState, useEffect } from "react";
import {
  Avatar,
  Popover,
  Button,
  Typography,
  Box,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Divider,
  Tooltip,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  Chip,
  Paper,
  InputAdornment,
  CircularProgress,
  Radio,
  RadioGroup,
  FormGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Check,
  ContentCopy,
  Link as LinkIcon,
  Mail,
  Close,
  PersonAdd,
  Security,
  RemoveRedEyeOutlined,
  EditOutlined,
  ExpandMoreOutlined,
  Delete,
  LinkOff,
  Refresh,
  ReplyOutlined,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import useAxios from "../../utils/useAxios";
import { SettingOutlined } from "@ant-design/icons";

const SharePopover = ({
  shareLink: initialShareLink,
  fileName,
  triggerLabel,
  contentTypeId,
  objectId,
  initialSharedWith = [],
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElSettings, setAnchorElSettings] = useState(null);
  const [sharedWith, setSharedWith] = useState(initialSharedWith);
  const [copied, setCopied] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(initialShareLink || "");
  const [isPublicLink, setIsPublicLink] = useState(false); // ✅ Add this
  const [publicShareId, setPublicShareId] = useState(null); // ✅ Store public share ID
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const api = useAxios();

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);
  const id = open ? "share-popover" : undefined;

  const handleClickSettings = (event) => {
    setAnchorEl(null);
    setAnchorElSettings(event.currentTarget);
  };
  const handleCloseSettings = (event) => {
    setAnchorEl(event.currentTarget);
    setAnchorElSettings(null);
  };
  const openSettings = Boolean(anchorElSettings);
  const settingsId = openSettings ? "share-popover" : undefined;

  // Fetch existing shares
  useEffect(() => {
    const fetchExistingShares = async () => {
      if (!contentTypeId || !objectId) return;

      try {
        const res = await api.get(`/shares/${contentTypeId}/${objectId}/`);

        if (res.data && res.data.length > 0) {
          // ✅ Get ONLY the public share link
          const publicShare = res.data.find(
            (s) => s.is_public_link && s.share_link_enabled && s.share_link,
          );

          if (publicShare) {
            setGeneratedLink(publicShare.share_link);
            setIsPublicLink(true);
            setPublicShareId(publicShare.id); // Store ID for regenerate/disable
          } else {
            setGeneratedLink("");
            setIsPublicLink(false);
            setPublicShareId(null);
          }

          // Get unique list of shared users (exclude public link)
          const uniqueShares = res.data
            .filter((share) => !share.is_public_link) // ✅ Exclude public link from user list
            .reduce((acc, share) => {
              if (!acc.find((item) => item.email === share.email)) {
                acc.push({
                  name: share.email.split("@")[0],
                  email: share.email,
                  access: share.access_rights,
                  created: share.created,
                });
              }
              return acc;
            }, []);
          setSharedWith(uniqueShares);
        }
      } catch (err) {
        console.error("Failed to fetch existing shares:", err);
      }
    };

    if (open) {
      fetchExistingShares();
    }
  }, [contentTypeId, objectId, api, open]);

  // Formik validation and submission
  const formik = useFormik({
    initialValues: {
      emails: "",
      permission: "Viewer",
      enableLink: true,
    },
    validationSchema: Yup.object({
      emails: Yup.string().test(
        "valid-emails",
        "All emails must be valid",
        (value) => {
          if (!value || value.trim() === "") return true; // Allow empty
          const emailList = value
            .split(/[,\s]+/)
            .filter((e) => e.trim() !== "");
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          return emailList.every((email) => emailRegex.test(email));
        },
      ),
    }),
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      // Validate that either link is enabled or emails are provided
      if (
        !values.enableLink &&
        (!values.emails || values.emails.trim() === "")
      ) {
        setToast({
          open: true,
          severity: "error",
          message:
            "Please enable link sharing or enter at least one email address",
        });
        return;
      }

      setLoading(true);

      // Parse emails
      const emails = values.emails
        .split(/[,\s]+/)
        .filter((e) => e.trim() !== "")
        .map((e) => e.trim().toLowerCase());

      const results = {
        success: [],
        failed: [],
        alreadyShared: [],
      };

      try {
        // Process each email individually
        for (let email of emails) {
          // Check if already shared
          const alreadyExists = sharedWith.find((s) => s.email === email);
          if (alreadyExists) {
            results.alreadyShared.push(email);
            continue;
          }

          try {
            const response = await api.post(
              `/shares/${contentTypeId}/${objectId}/`,
              {
                email: email,
                access_rights: values.permission,
                share_link_enabled: values.enableLink,
              },
            );

            // Only set link if it's a public link (no email)
            if (response.data.share_link && response.data.is_public_link) {
              setGeneratedLink(response.data.share_link);
              setIsPublicLink(true);
              setPublicShareId(response.data.id);
            }

            results.success.push(email);
          } catch (err) {
            console.error(`Failed to share with ${email}:`, err);
            results.failed.push(email);
          }
        }

        // If only enabling link (no emails)
        if (emails.length === 0 && values.enableLink) {
          try {
            // Create a share entry for the current user to generate the link
            const response = await api.post(
              `/shares/${contentTypeId}/${objectId}/`,
              {
                access_rights: values.permission,
                share_link_enabled: true,
              },
            );
            if (response.data.share_link) {
              setGeneratedLink(response.data.share_link);
              setIsPublicLink(true);
              setPublicShareId(response.data.id);
              setToast({
                open: true,
                severity: "success",
                message: "Public share link generated successfully!",
              });
            }
          } catch (err) {
            console.error("Failed to generate share link:", err);
            setToast({
              open: true,
              severity: "error",
              message: "Failed to generate share link",
            });
          }
        }

        // Update shared list with successful emails
        if (results.success.length > 0) {
          setSharedWith((prev) => {
            const newEmails = results.success.map((email) => ({
              name: email.split("@")[0],
              email: email,
              access: values.permission,
            }));
            return [...prev, ...newEmails];
          });
        }

        // Show appropriate toast messages
        if (results.success.length > 0) {
          let message = `Successfully shared with ${results.success.length} user(s)`;

          if (results.alreadyShared.length > 0) {
            message += `. ${results.alreadyShared.length} already had access`;
          }

          if (results.failed.length > 0) {
            message += `. ${results.failed.length} failed`;
          }

          setToast({
            open: true,
            severity: results.failed.length > 0 ? "warning" : "success",
            message: message,
          });

          // Reset form only on complete success
          if (results.failed.length === 0) {
            resetForm();
          }
        } else if (
          results.alreadyShared.length > 0 &&
          results.failed.length === 0
        ) {
          setToast({
            open: true,
            severity: "info",
            message: `All provided emails already have access`,
          });
        } else if (results.failed.length > 0 && results.success.length === 0) {
          setToast({
            open: true,
            severity: "error",
            message: `Failed to share with: ${results.failed.join(", ")}`,
          });
        }
      } catch (error) {
        console.error("Unexpected error during sharing:", error);
        setToast({
          open: true,
          severity: "error",
          message: "An unexpected error occurred",
        });
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  const handleCopy = () => {
    if (!generatedLink) return;
    navigator.clipboard.writeText(generatedLink).then(() => {
      setCopied(true);
      setToast({
        open: true,
        severity: "success",
        message: "Link copied to clipboard!",
      });
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const permissionOptions = [
    { value: "Owner", label: "Owner", subtitle: "Full control" },
    {
      value: "Editor",
      label: "Editor",
      subtitle: "Can edit",
      icon: <EditOutlined />,
    },
    {
      value: "Viewer",
      label: "Viewer",
      subtitle: "View only",
      icon: <RemoveRedEyeOutlined />,
    },
  ];

  // Link settings
  // Add these states at the top of your component
  const [linkAccessType, setLinkAccessType] = useState("public");
  const [settings, setSettings] = useState({
    allowDownload: true,
    requirePassword: false,
    expirationEnabled: false,
    expirationDate: "",
  });

  const handleRegenerateLink = async () => {
    if (!publicShareId) {
      setToast({
        open: true,
        severity: "error",
        message: "No public link to regenerate",
      });
      return;
    }

    try {
      const response = await api.post(
        `/shares/${publicShareId}/regenerate_link/`,
      ); // ✅ Use action endpoint
      setGeneratedLink(response.data.share_link);
      setToast({
        open: true,
        severity: "success",
        message: "Link regenerated successfully!",
      });
    } catch (err) {
      console.error("Failed to regenerate link:", err);
      setToast({
        open: true,
        severity: "error",
        message: "Failed to regenerate link",
      });
    }
  };

  const handleDisableLink = async () => {
    if (!publicShareId) {
      setToast({
        open: true,
        severity: "error",
        message: "No public link to disable",
      });
      return;
    }

    try {
      await api.delete(
        `/shares/${contentTypeId}/${objectId}/${publicShareId}/`,
      ); // ✅ Use proper endpoint
      setGeneratedLink("");
      setIsPublicLink(false);
      setPublicShareId(null);
      setToast({
        open: true,
        severity: "success",
        message: "Share link disabled",
      });
    } catch (err) {
      console.error("Failed to disable link:", err);
      setToast({
        open: true,
        severity: "error",
        message: "Failed to disable link",
      });
    }
  };

  const handleChangeAccess = async (email, newAccess) => {
    try {
      // Update access level for specific user
      await api.patch(`/shares/${contentTypeId}/${objectId}/${email}/`, {
        access_rights: newAccess,
      });

      setSharedWith((prev) =>
        prev.map((person) =>
          person.email === email ? { ...person, access: newAccess } : person,
        ),
      );

      setToast({
        open: true,
        severity: "success",
        message: `Access level updated for ${email}`,
      });
    } catch (err) {
      console.error("Failed to update access:", err);
      setToast({
        open: true,
        severity: "error",
        message: "Failed to update access level",
      });
    }
  };

  const handleRemoveAccess = async (email) => {
    // if (!confirm(`Remove access for ${email}?`)) return;

    try {
      await api.delete(`/shares/${contentTypeId}/${objectId}/${email}/`);
      setSharedWith((prev) => prev.filter((person) => person.email !== email));
      setToast({
        open: true,
        severity: "success",
        message: `Removed access for ${email}`,
      });
    } catch (err) {
      console.error("Failed to remove access:", err);
      setToast({
        open: true,
        severity: "error",
        message: "Failed to remove access",
      });
    }
  };

  const handleAddPeople = () => {
    handleCloseSettings();
    // Open the main share popover to add people
    setAnchorEl(document.getElementById("share-button"));
  };

  const handleSaveSettings = async () => {
    try {
      await api.patch(`/shares/${publicShareId}/`, {
        link_access_type: linkAccessType,
        settings: settings,
      });

      setToast({
        open: true,
        severity: "success",
        message: "Settings saved successfully!",
      });
      handleCloseSettings();
    } catch (err) {
      console.error("Failed to save settings:", err);
      setToast({
        open: true,
        severity: "error",
        message: "Failed to save settings",
      });
    }
  };

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      <Typography
        aria-describedby={id}
        onClick={(e) => {
          e.stopPropagation();
          handleClick(e);
        }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          cursor: "pointer",
          transition: "all 0.2s ease",
          "&:hover": {
            color: "#006b69",
            transform: "translateY(-1px)",
          },
        }}
      >
        <ReplyOutlined
          sx={{
            transform: "scaleX(-1)",
            color: "#008d8a",
            fontSize: 20,
            mr: 1,
          }}
        />
        {triggerLabel || "Share"}
      </Typography>

      {/* share popover */}
      <Popover
        id={id}
        open={open}
        anchorReference="anchorPosition"
        anchorPosition={{
          top: window.innerHeight / 2,
          left: window.innerWidth / 2,
        }}
        onClose={handleClose}
        anchorOrigin={{ vertical: "center", horizontal: "center" }}
        transformOrigin={{ vertical: "center", horizontal: "center" }}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          elevation: 8,
          onClick: (e) => e.stopPropagation(),
          sx: {
            borderRadius: 3,
            minWidth: 350,
            maxWidth: 500,
            mt: 1,
            background: "linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%)",
            border: "1px solid rgba(0, 0, 0, 0.08)",
            overflow: "visible",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            pb: 2.5,
            position: "relative",
            borderRadius: "12px 12px 0 0",
            background: "linear-gradient(135deg, #008d8a 0%, #00b3b1 100%)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h5"
                color={"white"}
                sx={{ fontWeight: "bold", mb: 0.5 }}
              >
                Share {fileName || "Untitled"}
              </Typography>
            </Box>
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{
                "&:hover": { bgcolor: "rgba(0,0,0,0.1)" },
                color: "white",
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* Share via Link Section */}
          <Paper
            elevation={0}
            sx={{
              bgcolor: "#f0f7f7",
              borderRadius: 2,
              p: 2.5,
              mb: 3,
              border: "1px solid #e0eded",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LinkIcon sx={{ color: "#008d8a", fontSize: 20 }} />
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 600, color: "#2c3e50" }}
                >
                  Share Link
                </Typography>
              </Box>
              <Switch
                name="enableLink"
                checked={formik.values.enableLink}
                onChange={formik.handleChange}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#008d8a",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#008d8a",
                  },
                }}
              />
            </Box>

            {formik.values.enableLink && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  bgcolor: "white",
                  borderRadius: 1.5,
                  p: 1,
                  border: "1px solid #d0e5e5",
                }}
              >
                <TextField
                  variant="standard"
                  size="small"
                  fullWidth
                  value={generatedLink || "Click 'Share' to generate link..."}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                    sx: {
                      fontSize: "0.85rem",
                      color: generatedLink ? "#5a6c7d" : "#999",
                      px: 1,
                    },
                  }}
                />
                <Tooltip title={copied ? "Copied!" : "Copy link"} arrow>
                  <IconButton
                    onClick={handleCopy}
                    disabled={!generatedLink}
                    size="small"
                    sx={{
                      bgcolor: copied ? "#4caf50" : "#008d8a",
                      color: "white",
                      "&:hover": {
                        bgcolor: copied ? "#45a049" : "#006b69",
                      },
                      "&:disabled": {
                        bgcolor: "#e0e0e0",
                        color: "#999",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    {copied ? (
                      <Check fontSize="small" />
                    ) : (
                      <ContentCopy fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            {formik.values.enableLink && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 1,
                }}
              >
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Anyone with this link will have {formik.values.permission}{" "}
                  access
                </Typography>

                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClickSettings(e);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    cursor: "pointer",
                    color: "#008d8a",
                  }}
                >
                  <SettingOutlined style={{ fontSize: 14 }} />
                  <Typography variant="caption">Link settings</Typography>
                </Box>
              </Box>
            )}
          </Paper>

          {/* Access Rights */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
            >
              <Security sx={{ color: "#008d8a", fontSize: 20 }} />
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: "#2c3e50" }}
              >
                Default access rights
              </Typography>
            </Box>
            <FormControl fullWidth size="small">
              <Select
                name="permission"
                value={formik.values.permission}
                onChange={formik.handleChange}
                sx={{
                  borderRadius: 2,
                  bgcolor: "white",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#008d8a",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#008d8a",
                  },
                }}
              >
                {permissionOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        gap: 2,
                      }}
                    >
                      <Typography sx={{ fontSize: "1.2rem" }}>
                        {option.icon}
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {option.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          {option.subtitle}
                        </Typography>
                      </Box>
                      {formik.values.permission === option.value && (
                        <Check sx={{ color: "#008d8a", fontSize: 18 }} />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Invite People */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
            >
              <PersonAdd sx={{ color: "#008d8a", fontSize: 20 }} />
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: "#2c3e50" }}
              >
                Invite people
              </Typography>
            </Box>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Enter email addresses..."
              name="emails"
              value={formik.values.emails}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              multiline
              maxRows={3}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Mail sx={{ color: "#95a5a6", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "white",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#008d8a",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#008d8a",
                  },
                },
              }}
            />
            {formik.touched.emails && formik.errors.emails && (
              <Typography
                variant="caption"
                sx={{ color: "#f44336", mt: 0.5, display: "block", ml: 1 }}
              >
                {formik.errors.emails}
              </Typography>
            )}
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", mt: 0.5, display: "block", ml: 1 }}
            >
              Separate multiple emails with commas or spaces. They'll receive an
              email invitation.
            </Typography>
          </Box>

          {/* Shared With List */}
          {sharedWith.length > 0 && (
            <Box
              sx={{
                bgcolor: "#f8f9fa",
                borderRadius: 2,
                p: 2,
                mb: 3,
                border: "1px solid #e9ecef",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, mb: 1.5, color: "#2c3e50" }}
              >
                People with access ({sharedWith.length})
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {sharedWith.map((person, idx) => (
                  <Chip
                    key={idx}
                    avatar={
                      <Avatar
                        sx={{
                          bgcolor: "#008d8a",
                          width: 28,
                          height: 28,
                          fontSize: "0.75rem",
                        }}
                      >
                        {person.name[0]?.toUpperCase()}
                      </Avatar>
                    }
                    label={`${person.email} (${person.access})`}
                    size="small"
                    sx={{
                      bgcolor: "white",
                      border: "1px solid #e0e0e0",
                      "& .MuiChip-label": {
                        fontSize: "0.75rem",
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              fullWidth
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 500,
                borderColor: "#e0e0e0",
                color: "#5a6c7d",
                "&:hover": {
                  borderColor: "#008d8a",
                  bgcolor: "rgba(0, 141, 138, 0.04)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={formik.handleSubmit}
              disabled={loading || formik.isSubmitting}
              fullWidth
              startIcon={
                loading && <CircularProgress size={16} color="inherit" />
              }
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "#008d8a",
                boxShadow: "0 4px 12px rgba(0, 141, 138, 0.3)",
                "&:hover": {
                  bgcolor: "#006b69",
                  boxShadow: "0 6px 16px rgba(0, 141, 138, 0.4)",
                },
                "&:disabled": {
                  bgcolor: "#e0e0e0",
                },
              }}
            >
              {loading ? "Sharing..." : "Share"}
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* share link settings popover */}
      {/* share link settings popover */}
      <Popover
        id={settingsId}
        open={openSettings}
        anchorReference="anchorPosition"
        anchorPosition={{
          top: window.innerHeight / 2,
          left: window.innerWidth / 2,
        }}
        onClose={handleCloseSettings}
        anchorOrigin={{ vertical: "center", horizontal: "center" }}
        transformOrigin={{ vertical: "center", horizontal: "center" }}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          elevation: 8,
          onClick: (e) => e.stopPropagation(),
          sx: {
            borderRadius: 3,
            minWidth: 450,
            maxWidth: 550,
            maxHeight: "90vh", // ✅ Limit maximum height
            display: "flex",
            flexDirection: "column",
            background: "linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%)",
            border: "1px solid rgba(0, 0, 0, 0.08)",
            overflow: "hidden", // ✅ Hide overflow on parent
          },
        }}
      >
        {/* Header - Fixed at top */}
        <Box
          sx={{
            p: 3,
            pb: 2.5,
            position: "relative",
            borderRadius: "12px 12px 0 0",
            flexShrink: 0, // ✅ Prevent header from shrinking
            background: "linear-gradient(135deg, #008d8a 0%, #00b3b1 100%)",
              color: "white",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 0.5 }}
              >
                Shared Link Settings
              </Typography>
              <Typography variant="caption">
                Manage who can access this resource
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseSettings}
              size="small"
              sx={{
                "&:hover": { bgcolor: "rgba(0,0,0,0.1)", color: "white" },
              }}
            >
              <Close fontSize="small" sx={{ color: "white" }} />
            </IconButton>
          </Box>
        </Box>

        <Divider />

        {/* Scrollable Content Area */}
        <Box
          sx={{
            p: 3,
            overflowY: "auto", // ✅ Enable vertical scrolling
            flexGrow: 1, // ✅ Take remaining space
            // ✅ Custom scrollbar styling
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#008d8a",
              borderRadius: "10px",
              "&:hover": {
                backgroundColor: "#006b69",
              },
            },
            // ✅ Firefox scrollbar
            scrollbarWidth: "thin",
            scrollbarColor: "#008d8a #f1f1f1",
          }}
        >
          {/* Current Link Status */}
          {generatedLink && (
            <Paper
              elevation={0}
              sx={{
                bgcolor: "#f0f7f7",
                borderRadius: 2,
                p: 2.5,
                mb: 3,
                border: "1px solid #e0eded",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LinkIcon sx={{ color: "#008d8a", fontSize: 20 }} />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: "#2c3e50" }}
                  >
                    Active Share Link
                  </Typography>
                </Box>
                <Chip
                  label="Active"
                  size="small"
                  sx={{
                    bgcolor: "#4caf50",
                    color: "white",
                    fontWeight: 500,
                    fontSize: "0.7rem",
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  bgcolor: "white",
                  borderRadius: 1.5,
                  p: 1,
                  mb: 1.5,
                  border: "1px solid #d0e5e5",
                }}
              >
                <TextField
                  variant="standard"
                  size="small"
                  fullWidth
                  value={generatedLink}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                    sx: {
                      fontSize: "0.85rem",
                      color: "#5a6c7d",
                      px: 1,
                    },
                  }}
                />
                <Tooltip title={copied ? "Copied!" : "Copy link"} arrow>
                  <IconButton
                    onClick={handleCopy}
                    size="small"
                    sx={{
                      bgcolor: copied ? "#4caf50" : "#008d8a",
                      color: "white",
                      "&:hover": {
                        bgcolor: copied ? "#45a049" : "#006b69",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    {copied ? (
                      <Check fontSize="small" />
                    ) : (
                      <ContentCopy fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Link Actions */}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  startIcon={<Refresh />}
                  onClick={handleRegenerateLink}
                  sx={{
                    textTransform: "none",
                    color: "#008d8a",
                    fontSize: "0.8rem",
                    "&:hover": { bgcolor: "rgba(0, 141, 138, 0.08)" },
                  }}
                >
                  Regenerate Link
                </Button>
                <Button
                  size="small"
                  startIcon={<LinkOff />}
                  onClick={handleDisableLink}
                  sx={{
                    textTransform: "none",
                    color: "#f44336",
                    fontSize: "0.8rem",
                    "&:hover": { bgcolor: "rgba(244, 67, 54, 0.08)" },
                  }}
                >
                  Disable Link
                </Button>
              </Box>
            </Paper>
          )}

          {/* Link Access Control */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, mb: 1.5, color: "#2c3e50" }}
            >
              Link Access Type
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup
                value={linkAccessType}
                onChange={(e) => setLinkAccessType(e.target.value)}
              >
                <FormControlLabel
                  value="public"
                  control={
                    <Radio
                      sx={{
                        color: "#008d8a",
                        "&.Mui-checked": { color: "#008d8a" },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Anyone with the link
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Anyone in your organization with the link can access
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="restricted"
                  control={
                    <Radio
                      sx={{
                        color: "#008d8a",
                        "&.Mui-checked": { color: "#008d8a" },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Restricted
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Only people added below can access
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* People with Access */}
          {sharedWith.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#2c3e50" }}
                >
                  People with access ({sharedWith.length})
                </Typography>
                <Tooltip title="Add people">
                  <IconButton
                    size="small"
                    onClick={handleAddPeople}
                    sx={{ color: "#008d8a" }}
                  >
                    <PersonAdd fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box>
                {sharedWith.map((person, idx) => (
                  <Paper
                    key={idx}
                    elevation={0}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 1.5,
                      mb: 1,
                      bgcolor: "white",
                      border: "1px solid #e9ecef",
                      borderRadius: 1.5,
                      transition: "all 0.2s",
                      "&:hover": {
                        bgcolor: "#f8f9fa",
                        borderColor: "#008d8a",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        flex: 1,
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: "#008d8a",
                          width: 36,
                          height: 36,
                          fontSize: "0.9rem",
                        }}
                      >
                        {person.name[0]?.toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {person.email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Added {new Date(person.created).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Select
                        size="small"
                        value={person.access}
                        onChange={(e) =>
                          handleChangeAccess(person.email, e.target.value)
                        }
                        sx={{
                          fontSize: "0.8rem",
                          minWidth: 100,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e0e0e0",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#008d8a",
                          },
                        }}
                      >
                        <MenuItem value="Owner">Owner</MenuItem>
                        <MenuItem value="Editor">Editor</MenuItem>
                        <MenuItem value="Viewer">Viewer</MenuItem>
                      </Select>

                      <Tooltip title="Remove access">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveAccess(person.email)}
                          sx={{
                            color: "#f44336",
                            "&:hover": { bgcolor: "rgba(244, 67, 54, 0.08)" },
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}

          {/* Advanced Settings */}
          <Accordion
            elevation={0}
            sx={{
              bgcolor: "transparent",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreOutlined />}
              sx={{
                px: 0,
                minHeight: 40,
                "& .MuiAccordionSummary-content": { my: 0 },
              }}
            >
              <Typography variant="body2" fontWeight={600} color="#2c3e50">
                Advanced Settings
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0 }}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.allowDownload}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          allowDownload: e.target.checked,
                        })
                      }
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#008d8a",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          { backgroundColor: "#008d8a" },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Allow viewers to download
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.requirePassword}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          requirePassword: e.target.checked,
                        })
                      }
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#008d8a",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          { backgroundColor: "#008d8a" },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Require password to access
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.expirationEnabled}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          expirationEnabled: e.target.checked,
                        })
                      }
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#008d8a",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          { backgroundColor: "#008d8a" },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2">Set link expiration</Typography>
                  }
                />

                {settings.expirationEnabled && (
                  <TextField
                    type="datetime-local"
                    size="small"
                    fullWidth
                    label="Expires on"
                    value={settings.expirationDate}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        expirationDate: e.target.value,
                      })
                    }
                    sx={{ mt: 1, ml: 4 }}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              </FormGroup>
            </AccordionDetails>
          </Accordion>

          <Divider sx={{ my: 2 }} />

          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleCloseSettings}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 500,
                borderColor: "#e0e0e0",
                color: "#5a6c7d",
                "&:hover": {
                  borderColor: "#008d8a",
                  bgcolor: "rgba(0, 141, 138, 0.04)",
                },
              }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSaveSettings}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "#008d8a",
                boxShadow: "0 4px 12px rgba(0, 141, 138, 0.3)",
                "&:hover": {
                  bgcolor: "#006b69",
                  boxShadow: "0 6px 16px rgba(0, 141, 138, 0.4)",
                },
              }}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          sx={{
            width: "100%",
            borderRadius: 2,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
          onClose={() => setToast({ ...toast, open: false })}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SharePopover;
